"use client";

import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@frontend/shared/components/ui/form";
import { Input } from "@frontend/shared/components/ui/input";
import { Textarea } from "@frontend/shared/components/ui/textarea";
import { cn } from "@frontend/shared/lib/utils";
import { useFormContext } from "react-hook-form";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import type { InputFieldProps } from "../../[clientSlug]/types";

interface InputComponentProps {
  style: string;
  props: InputFieldProps;
}

export function InputComponent({ style, props }: InputComponentProps) {
  const form = useFormContext();

  if (!form) {
    console.warn("InputComponent must be used within a form provider");
    return null;
  }

  const {
    inputId,
    fieldName,
    fieldType,
    placeholder,
    internalStyle,
    required,
  } = props;

  // Any type because we want the form to have any type of configurable field
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const renderInput = (field: any, style: string) => {
    switch (fieldType) {
      case "phone":
        return (
          <PhoneInput
            className={cn(style)}
            placeholder={placeholder || "(XXX) XXX-XXXX"}
            countryOptionsOrder={["US", "|", "..."]}
            addInternationalOption={false}
            defaultCountry="US"
            numberInputProps={{
              className: internalStyle,
            }}
            {...field}
          />
        );
      case "textarea":
        return (
          <Textarea
            id={inputId}
            placeholder={placeholder}
            className={cn(style)}
            {...field}
          />
        );
      case "email":
        return (
          <Input
            id={inputId}
            type="email"
            placeholder={placeholder || "Enter your email"}
            className={cn(
              "focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 font-suisse font-thin text-lg ",
              style,
            )}
            {...field}
          />
        );
      default:
        return (
          <Input
            id={inputId}
            type="text"
            placeholder={placeholder}
            className={cn(
              "focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 font-suisse font-thin text-lg ",
              style,
            )}
            {...field}
          />
        );
    }
  };

  // Determine FormItem classes based on field type and style
  const getFormItemClasses = () => {
    const hasFullHeight = style.includes("h-full") || style.includes("flex-1");

    if (fieldType === "textarea" && hasFullHeight) {
      return "flex flex-col h-full";
    }

    if (hasFullHeight) {
      return "h-full";
    }

    return "";
  };

  return (
    <FormField
      control={form.control}
      name={fieldName}
      render={({ field }) => (
        <FormItem className={getFormItemClasses()}>
          <FormControl>{renderInput(field, style)}</FormControl>
          <FormMessage className="ml-2" />
        </FormItem>
      )}
    />
  );
}
